import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { authApi } from '../api/auth';
import { AuthCard, AuthScreen, AuthTitle } from '../components/auth/AuthScreen';
import { Banner, Button, Field, Input } from '../components/ui';

type Step = 'email' | 'code' | 'password';

const TITLES: Record<Step, string> = {
  email: 'Reset your password',
  code: 'Enter the code',
  password: 'Set a new password',
};

export const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [shake, setShake] = useState(0);

  const fail = (msg: string) => {
    setError(msg);
    setShake((n) => n + 1);
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await authApi.forgotPassword(email.trim().toLowerCase());

      // Check if this is an OAuth-only account
      if (response.status === 'oauth_only') {
        setError(response.message);
        setLoading(false);
        return;
      }

      setStep('code');
    } catch (error: any) {
      fail(error.userMessage || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await authApi.verifyResetCode(email.trim().toLowerCase(), code.trim());
      setResetToken(response.resetToken);
      setStep('password');
    } catch (error: any) {
      fail(error.userMessage || 'Invalid or expired code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords
    if (newPassword.length < 8) return fail('Password must be at least 8 characters');
    if (!/[A-Za-z]/.test(newPassword)) return fail('Password must contain at least one letter');
    if (!/\d/.test(newPassword)) return fail('Password must contain at least one number');
    if (newPassword !== confirmPassword) return fail('Passwords do not match');

    setLoading(true);

    try {
      await authApi.resetPassword(resetToken, newPassword);
      // Success - navigate to login
      navigate('/login?reset=success');
    } catch (error: any) {
      fail(error.userMessage || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sub =
    step === 'email'
      ? "Enter the email you sign in with and we'll send you a reset code."
      : step === 'code'
        ? `We sent a 6-digit code to ${email}`
        : 'Use at least 8 characters with a letter and a number.';

  const eye = (
    <button
      type="button"
      onClick={() => setShowPassword(!showPassword)}
      aria-label={showPassword ? 'Hide passwords' : 'Show passwords'}
      aria-pressed={showPassword}
      className="w-9 h-9 rounded-[9px] flex items-center justify-center text-t4 hover:bg-fill2 hover:text-t1"
    >
      {showPassword ? <EyeOff size={18} strokeWidth={1.7} aria-hidden="true" /> : <Eye size={18} strokeWidth={1.7} aria-hidden="true" />}
    </button>
  );

  return (
    <AuthScreen>
      <AuthTitle key={step} title={TITLES[step]} sub={sub} />

      <AuthCard
        as="form"
        shake={shake}
        className="p-[26px] flex flex-col gap-[18px]"
        onSubmit={step === 'email' ? handleRequestCode : step === 'code' ? handleVerifyCode : handleResetPassword}
      >
        {error && (
          <Banner tone="acc" role="alert">
            {error}
          </Banner>
        )}

        {step === 'email' && (
          <>
            <Field label="Email" htmlFor="fp-email">
              <Input
                id="fp-email"
                type="email"
                height={48}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@church.org"
                autoComplete="email"
                disabled={loading}
                required
              />
            </Field>
            <Button type="submit" size="xl" shape="form" block loading={loading}>
              Send reset code
            </Button>
          </>
        )}

        {step === 'code' && (
          <>
            <Field label="Verification code" htmlFor="fp-code" hint="Check your email for the 6-digit code">
              <Input
                id="fp-code"
                type="text"
                inputMode="numeric"
                height={56}
                mono
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center !text-[22px] font-bold tracking-[.3em]"
                placeholder="000000"
                maxLength={6}
                autoComplete="one-time-code"
                disabled={loading}
                required
              />
            </Field>
            <Button type="submit" size="xl" shape="form" block loading={loading} disabled={code.length !== 6}>
              Verify code
            </Button>
            <button
              type="button"
              onClick={() => handleRequestCode({ preventDefault: () => {} } as React.FormEvent)}
              disabled={loading}
              className="text-[13.5px] text-t3 hover:text-t1 disabled:text-t5"
            >
              Didn't receive the code? Resend
            </button>
          </>
        )}

        {step === 'password' && (
          <>
            <Field label="New password" htmlFor="fp-new">
              <Input
                id="fp-new"
                type={showPassword ? 'text' : 'password'}
                height={48}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                disabled={loading}
                required
                trailing={eye}
              />
            </Field>
            <Field label="Confirm new password" htmlFor="fp-confirm">
              <Input
                id="fp-confirm"
                type={showPassword ? 'text' : 'password'}
                height={48}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Type it again"
                autoComplete="new-password"
                disabled={loading}
                required
              />
            </Field>
            <Button type="submit" size="xl" shape="form" block loading={loading}>
              Reset password
            </Button>
          </>
        )}
      </AuthCard>

      <button type="button" onClick={() => navigate('/login')} className="mt-6 text-[13.5px] text-link hover:text-acc">
        ← Back to log in
      </button>
    </AuthScreen>
  );
};
